import { useState } from "react";
import { toast } from "sonner";
import { Package, Mail, Users, HelpCircle, Search, Truck, ExternalLink } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  ["Are Ethos Research Labs Peptides Safe to Use?", "All peptides offered by us are laboratory-tested and manufactured to high-purity standards for in-vitro research applications only. These products are not intended for human or animal use and must not be used in the manufacture of food, pharmaceuticals, cosmetics, or diagnostic products. None of our peptides have been approved by the U.S. Food and Drug Administration (FDA)."],
  ["How can I contact Ethos Research Labs for help?", "For questions regarding our products, orders, or research materials, please contact our team through our website contact form or by email at ethos@ethosresearchlabs.com. We are committed to supporting qualified researchers and are available to assist with product-related inquiries and order support."],
  ["What are research peptides?", "Research peptides are precision-synthesized amino acid compounds utilized in scientific investigations to advance the understanding of biological systems. Within controlled in-vitro environments, they enable researchers to examine cellular behavior, molecular signaling pathways, and complex biochemical interactions. All peptides sold by Ethos Research Labs are provided solely for scientific research purposes. They are not FDA approved and are not intended for human consumption, animal use, therapeutic applications, or clinical procedures. These products are not intended to diagnose, treat, cure, or prevent any disease or medical condition."],
  ["Are your peptides tested for purity?", "Yes. Each production batch is independently analyzed by a U.S. based third-party laboratory to verify peptide purity, identity, and quality standards. Certificates of Analysis (COAs) are made available to provide researchers with documented verification and complete transparency regarding product consistency and performance."],
  ["Are your products legal to purchase in the U.S. and Canada?", "All products are intended solely for laboratory research purposes. It is the responsibility of the purchaser to verify and comply with all applicable local, state, and federal regulations prior to placing an order."],
  ["Who can order from Ethos Research Labs?", "Our products are available to qualified researchers, laboratories, institutions, and individuals conducting lawful scientific research. By placing an order, customers confirm that the materials will be used solely for research purposes."],
  ["Do you offer fast domestic shipping?", "Yes. We provide fast and dependable shipping throughout the United States. Most orders are processed promptly and dispatched within 1–2 business days to help ensure researchers receive their materials in a timely manner."],
  ["Can I track my order?", "Yes. Upon shipment, an email containing your tracking details will be sent to you, providing visibility into your order's transit status and delivery progress from dispatch to arrival."],
  ["Do you ship internationally?", "International shipping is available to select countries, subject to applicable regulations. It is the responsibility of the purchaser to ensure that the importation, possession, and use of research compounds comply with all local laws and requirements."],
  ["What payment methods do you accept?", "We utilize secure payment processing and offer a range of payment options to accommodate our customers. Available methods will be clearly displayed during checkout."],
  ["Why choose Ethos Research Labs?", "Ethos Research Labs is dedicated to supplying high-quality research compounds backed by rigorous quality standards, dependable shipping, and transparent third-party testing. Through our commitment to product integrity, secure packaging, and responsive customer service, we strive to be a trusted resource for the research community."],
  ["What will the charge look like on my bank statement?", "Charges from Ethos Research Labs will appear on your credit or debit card statement as \u201cETHOS RESEARCH LABS\u201d. If you have any questions about a charge, please contact our customer support before initiating a dispute so we can assist you promptly."],
  ["How do I file a complaint or dispute a charge?", "Ethos Research Labs maintains a dedicated channel for complaints, billing concerns, and disputes. Please contact us at ethos@ethosresearchlabs.com or through our website contact form before contacting your bank. We acknowledge every complaint promptly and aim to provide a resolution within seven (7) business days. Reaching out to us directly is the fastest way to resolve refunds (where applicable), re-shipments, or billing questions."],
];

function Shell({ icon: Icon, eyebrow, title, children }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <span className="flex items-center gap-2 eyebrow"><Icon className="w-3.5 h-3.5" /> {eyebrow}</span>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-3">{title}</h1>
      <div className="mt-8">{children}</div>
    </div>
  );
}

export default function SimplePage({ page }) {
  const [orderNo, setOrderNo] = useState("");
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch soon");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (page === "faq") {
    return (
      <Shell icon={HelpCircle} eyebrow="Help Center" title="Frequently asked questions">
        <Accordion type="single" collapsible className="card-soft rounded-2xl px-6">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-display font-bold text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Shell>
    );
  }

  if (page === "contact") {
    return (
      <Shell icon={Mail} eyebrow="Get in touch" title="Contact us">
        <p className="text-muted-foreground -mt-4 mb-6 max-w-xl">Questions about documentation, bulk research orders, or your account? Send us a message and our team will respond within one business day.</p>
        <form onSubmit={submitContact} className="card-soft rounded-2xl p-6 space-y-4 max-w-xl" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 bg-white border-border" data-testid="contact-name" required />
            <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 bg-white border-border" data-testid="contact-email" required />
          </div>
          <Textarea placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="min-h-32 bg-white border-border" data-testid="contact-message" required />
          <button type="submit" disabled={sending} className="h-11 px-6 rounded-xl btn-ink font-display font-bold disabled:opacity-60" data-testid="contact-submit">{sending ? "Sending…" : "Send message"}</button>
        </form>
      </Shell>
    );
  }

  if (page === "affiliates") {
    return (
      <Shell icon={Users} eyebrow="Partner program" title="Become an affiliate">
        <p className="text-muted-foreground -mt-4 mb-8 max-w-xl">Earn commission by referring qualified researchers and labs to Ethos Research Labs. Transparent tracking, monthly payouts, and dedicated support.</p>
        <div className="grid sm:grid-cols-3 gap-5 mb-8">
          {[["20%", "Commission per sale"], ["60-day", "Cookie window"], ["Monthly", "USDC payouts"]].map(([a, b], i) => (
            <div key={i} className="card-soft rounded-2xl p-6 text-center">
              <p className="font-display font-extrabold text-3xl" style={{ color: "var(--gold)" }}>{a}</p>
              <p className="text-sm text-muted-foreground mt-1">{b}</p>
            </div>
          ))}
        </div>
        <button onClick={() => toast.success("Application received — we'll review and reach out")} className="h-12 px-7 rounded-xl btn-ink font-display font-bold" data-testid="affiliate-apply">Apply to join</button>
      </Shell>
    );
  }

  // track
  const track = async (e) => {
    e.preventDefault();
    setNotFound(false); setOrder(null);
    try {
      const r = await api.get(`/orders/${orderNo.trim()}`);
      setOrder(r.data);
    } catch {
      setNotFound(true);
    }
  };

  return (
    <Shell icon={Package} eyebrow="Order status" title="Track your order">
      <form onSubmit={track} className="flex gap-2 max-w-md" data-testid="track-form">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Order number (ETH-XXXXXXXX)" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} className="pl-11 h-12 bg-white border-border" data-testid="track-input" required />
        </div>
        <button type="submit" className="h-12 px-6 rounded-xl btn-ink font-display font-bold" data-testid="track-submit">Track</button>
      </form>

      {notFound && <p className="mt-6 text-muted-foreground" data-testid="track-notfound">No order found with that number.</p>}
      {order && (
        <div className="card-soft rounded-2xl p-6 mt-8 max-w-md" data-testid="track-result">
          <div className="flex items-center justify-between">
            <div><p className="text-xs text-muted-foreground">Order</p><p className="font-display font-bold">{order.order_number}</p></div>
            <span className="pill px-3 py-1 text-xs font-bold bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 capitalize">{order.status}</span>
          </div>
          <div className="border-t border-border mt-4 pt-4 space-y-2">
            {(order.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm"><span className="text-muted-foreground">{i.name} × {i.quantity}</span><span className="font-semibold">{formatMoney(i.price * i.quantity)}</span></div>
            ))}
            <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Total</span><span className="font-display font-bold">{formatMoney(order.total)}</span></div>
          </div>
          {order.tracking ? (
            <a href={order.tracking.url} target="_blank" rel="noopener noreferrer" data-testid="track-carrier-link"
              className="mt-5 flex items-center justify-between gap-3 rounded-xl border-2 border-[#c59b27]/40 bg-[#fbf6ec] px-4 py-3 hover:bg-[#f7efdd] transition-colors">
              <span className="min-w-0">
                <span className="eyebrow flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {order.tracking.carrier_label} tracking</span>
                <span className="block font-semibold text-sm mt-0.5 break-all">{order.tracking.tracking_number}</span>
              </span>
              <span className="shrink-0 inline-flex items-center gap-1 text-sm font-display font-bold" style={{ color: "var(--gold)" }}>Track <ExternalLink className="w-4 h-4" /></span>
            </a>
          ) : (
            <p className="mt-5 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="track-no-tracking"><Truck className="w-3.5 h-3.5" /> A tracking number will appear here once your order ships.</p>
          )}
        </div>
      )}
    </Shell>
  );
}
